import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * Spin & Win — viral QR prize wheel. Customers scan the QR on the café TV,
 * spin a wheel on their phone, and winners are celebrated live on the screens.
 * Backend: SpinWheelController (one wheel per company, get-or-created).
 *
 *   GET    /spin/settings              → { data: { wheel, prizes, spin_url } }
 *   PUT    /spin/settings              update config (+ toggles TV banner)
 *   GET    /spin/prizes                → { data: { prizes } }
 *   POST   /spin/prizes                add segment
 *   PUT    /spin/prizes/{id}           edit segment
 *   DELETE /spin/prizes/{id}           remove segment
 *   POST   /spin/prizes/reorder        { order: [id, ...] }
 *   GET    /spin/results?period&redeemed → { data: { results } }
 *   GET    /spin/stats                 → { data: { stats } }
 *   POST   /spin/redeem/{code}         cashier marks code used
 *
 * All CompanyResource-wrapped → Laravel nests under a top-level `data` key;
 * the hooks unwrap with `res?.data ?? res` (safe if wrapping is ever off).
 */

const SETTINGS_KEY = 'spinSettings';
const PRIZES_KEY   = 'spinPrizes';
const RESULTS_KEY  = 'spinResults';
const STATS_KEY    = 'spinStats';

export const useGetSpinSettings = () =>
  useQuery(
    SETTINGS_KEY,
    async () => {
      const res = (await axiosInstance.get('/spin/settings')).data;
      return res?.data ?? res;
    },
    { staleTime: 30_000 }
  );

export const useGetSpinPrizes = () =>
  useQuery(
    PRIZES_KEY,
    async () => {
      const res = (await axiosInstance.get('/spin/prizes')).data;
      return res?.data ?? res;
    },
    { staleTime: 30_000 }
  );

export const useGetSpinStats = () =>
  useQuery(
    STATS_KEY,
    async () => {
      const res = (await axiosInstance.get('/spin/stats')).data;
      return res?.data ?? res;
    },
    // Poll so headline counters tick up as new spins land — no manual refresh.
    { refetchInterval: 5_000, staleTime: 4_000 }
  );

export const useGetSpinResults = (filters = {}) =>
  useQuery(
    [RESULTS_KEY, filters],
    async ({ queryKey }) => {
      const res = (await axiosInstance.get('/spin/results', { params: queryKey[1] })).data;
      return res?.data ?? res;
    },
    // Live-ish "Recent Results": new spins appear within ~5s automatically.
    { keepPreviousData: true, staleTime: 4_000, refetchInterval: 5_000 }
  );

export const useUpdateSpinSettings = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.put('/spin/settings', payload), {
    onSuccess: () => {
      qc.invalidateQueries(SETTINGS_KEY);
      showToast('success', 'Spin & Win', 'Settings saved.');
    },
    onError: (err) => showToast('error', 'Spin & Win', err.response?.data?.message),
  });
};

export const useAddSpinPrize = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/spin/prizes', payload), {
    onSuccess: () => {
      qc.invalidateQueries(PRIZES_KEY);
      qc.invalidateQueries(SETTINGS_KEY);
      showToast('success', 'Spin & Win', 'Prize added.');
    },
    onError: (err) => showToast('error', 'Spin & Win', err.response?.data?.message),
  });
};

export const useUpdateSpinPrize = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    ({ id, ...payload }) => axiosInstance.put(`/spin/prizes/${id}`, payload),
    {
      onSuccess: () => {
        qc.invalidateQueries(PRIZES_KEY);
        qc.invalidateQueries(SETTINGS_KEY);
        showToast('success', 'Spin & Win', 'Prize updated.');
      },
      onError: (err) => showToast('error', 'Spin & Win', err.response?.data?.message),
    }
  );
};

export const useDeleteSpinPrize = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/spin/prizes/${id}`), {
    onSuccess: () => {
      qc.invalidateQueries(PRIZES_KEY);
      qc.invalidateQueries(SETTINGS_KEY);
      showToast('success', 'Spin & Win', 'Prize removed.');
    },
    onError: (err) => showToast('error', 'Spin & Win', err.response?.data?.message),
  });
};

/**
 * Read-only pre-check for the cashier confirmation step (QR scan or manual).
 * Returns the unwrapped detail body: { status, valid, prize_name, player_name,
 * won_at, expires_at, is_expired, is_redeemed, msg, prize_code }. Does NOT
 * mark the code used — that's the separate redeem call after Confirm.
 */
export const useVerifySpinCode = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    async (code) => {
      const res = (await axiosInstance.get(`/spin/verify/${encodeURIComponent(code)}`)).data;
      return res?.data ?? res;
    },
    { onError: (err) => showToast('error', 'Spin & Win', err.response?.data?.message) }
  );
};

export const useRedeemSpinCode = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    async (code) => {
      const res = (await axiosInstance.post(`/spin/redeem/${encodeURIComponent(code)}`)).data;
      return res?.data ?? res;
    },
    {
      onSuccess: (body) => {
        qc.invalidateQueries(RESULTS_KEY);
        qc.invalidateQueries(STATS_KEY);
        if (body?.status === 200) {
          showToast('success', 'Spin & Win', `Redeemed: ${body.prize_name ?? ''} 🎉`);
        } else {
          showToast('warn', 'Spin & Win', body?.msg ?? 'Could not redeem code.');
        }
      },
      onError: (err) => showToast('error', 'Spin & Win', err.response?.data?.message),
    }
  );
};
